import '@/css/classes/datadisplay.scss';
import svgSortAmountDesc from '@static/icons/sort-amount-desc.svg?raw';
import svgSortAmountAsc from '@static/icons/sort-amount-asc.svg?raw';
import svgSquareX from '@static/icons/square-xmark.svg?raw';
import svgBookmark from '@static/icons/bookmark.svg?raw';

import {
    deleteValueFromArray,
    genId,
    toSnakeCase,
} from '@/js/util.js';

import {
    assert,
    assertArray,
    assertIn,
    assertIncludes,
    assertInteger,
    assertNonblank,
    assertNonemptyArray,
    assertNonemptyString,
    assertObject,
    isFloatStrict,
    tryParseIntStrict,
} from '@/js/util/asserts.js';

import { HexColor } from '@/js/util/shared_constants.js';


export class DataDisplay {
    // Card field styling
    static name_field_names = ['project name', 'name', 'label'];
    static id_field_names = ['id', 'tree id'];
    static comments_field_names = ['notes', 'comments'];

    constructor(opts) {
        assertIn('name', opts);
        assertIn('field_names', opts);
        assertIn('parent_elem', opts);
        assertNonemptyString(opts['name']);
        assertNonemptyArray(opts['field_names']);
        assert(opts['parent_elem'] instanceof Node);

        this.id = 'datadisplay_'+genId();

        this.parent_elem = opts['parent_elem']; //Node
        this.name = opts['name']; // Must be sanitized/safe
        this.field_names = opts['field_names']; //array of strings

        if('help_text' in opts) { assertNonemptyString(opts['help_text']); }
        if('entries_per_page' in opts) { assertInteger(opts['entries_per_page']); }
        if('sort_by' in opts) { assertNonemptyString(opts['sort_by']); }
        if('sort_direction' in opts) { assertIncludes(['desc', 'asc'], opts['sort_direction']); }
        if('do_not_sort_by' in opts) { assertArray(opts['do_not_sort_by']); }
        if('heading_background_color' in opts) { assertNonemptyString(opts['heading_background_color']); }
        if('heading_text_color' in opts) { assertNonemptyString(opts['heading_text_color']); }
        if('entry_hover_lightness_percent' in opts) { assertInteger(opts['entry_hover_lightness_percent']); }
        if('field_width_overrides' in opts) { assertObject(opts['field_width_overrides']); }

        this.help_text = opts['help_text'] ?? ''; //string, Must be sanitized/safe
        this.entries_per_page = opts['entries_per_page'] ?? 15; //int
        this.sort_by = opts['sort_by'] ?? null; //string (snake case field name) or null
        this.sort_direction = opts['sort_direction'] ?? 'desc'; //string
        this.do_not_sort_by = opts['do_not_sort_by'] ?? []; //array of strings
        this.heading_background_color = opts['heading_background_color'] ?? HexColor.LightBlue;
        this.heading_text_color = opts['heading_text_color'] ?? HexColor.Black;
        this.entry_hover_lightness_percent = opts['entry_hover_lightness_percent'] ?? 10;
        this.field_width_overrides = opts['field_width_overrides'] ?? null;

        this._entry_data = {}; // Map<id, {values, sort_values, search_str}>
        this._all_entry_ids = []; // Array of all IDs in insertion order
        this._filtered_entry_ids = []; // Array of IDs currently matching search/sort

        this._entry_click_callback = ((id) => {}); //function
        this._entry_render_callback = ((nodes) => {}); //function
        this._field_map = {};
        this.field_names.forEach((f, i) => this._field_map[toSnakeCase(f)] = i);
        this._num_fields = this.field_names.length;
        this.page = 0;
        this.max_page = 0;
        this._search_text = '';
        this._needs_reorder = false;
    }

    search(search_text) {
        search_text = search_text.toLowerCase()
        this._search_text = search_text.trim();
        this._search_box_elem.value = search_text;

        if(this._search_text === '') {
            this._filtered_entry_ids = [...this._all_entry_ids];
            this._search_clear_button.classList.add('hidden');
            this._search_bookmark_button.classList.add('hidden');
            this.clearUrlSearch();
        } else {
            this._filtered_entry_ids = this._all_entry_ids.filter(id => {
                return this._entry_data[id].search_str.includes(this._search_text);
            });
            this._search_clear_button.classList.remove('hidden');
            this._search_bookmark_button.classList.remove('hidden');

            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set('search', this._search_text);
            this._search_bookmark_button.href = newUrl.href;
        }

        this.reorder();
    }

    paginate() {
        const starting_idx = this.page * this.entries_per_page;
        const stopping_idx = starting_idx + this.entries_per_page;
        const ids_to_show = this._filtered_entry_ids.slice(starting_idx, stopping_idx);

        const table_fragment = document.createDocumentFragment();
        const card_fragment = document.createDocumentFragment();

        for(const id of ids_to_show) {
            const entry = this._entry_data[id];

            const row_elem = this._genRowElem(entry.values);
            row_elem.setAttribute('data-entry-id', id);
            table_fragment.appendChild(row_elem);

            const card_elem = this._genCardElem(entry.values);
            card_elem.setAttribute('data-entry-id', id);
            card_fragment.appendChild(card_elem);
        }

        this.table_body_elem.replaceChildren(table_fragment);
        this.cardlist_elem.replaceChildren(card_fragment);

        const total = this._filtered_entry_ids.length;
        this._pagination_showing.textContent = this.name + ((total === 1) ? (' 1') : ('s ' + Math.min(starting_idx + 1, total) + ' - ' + Math.min(stopping_idx, total)));
        this._pagination_pagenumber.textContent = this.page + 1;
        this._pagination_last.disabled = (this.page <= 0);
        this._pagination_next.disabled = (this.page >= this.max_page);

        this._checkForEntries();
        if(this._entry_render_callback) {
            this._entry_render_callback(this._container.querySelectorAll('.datadisplay-body-row, div.card'));
        }
    }

    reorder() {
        const field_index = this._field_map[this.sort_by] ?? 0;

        this._filtered_entry_ids.sort((a, b) => {
            const valA = this._entry_data[a].sort_values[field_index];
            const valB = this._entry_data[b].sort_values[field_index];

            if(valA == null && valB == null) { return 0; }
            if(valA == null) { return 1; }
            if(valB == null) { return -1; }

            let result;
            if(isFloatStrict(valA) && isFloatStrict(valB)) {
                result = parseFloat(valA) - parseFloat(valB);
            } else {
                result = valA.toString().localeCompare(valB.toString());
            }

            return (this.sort_direction === 'desc' ? -result : result);
        });

        this._needs_reorder = false;
        this.page = 0;
        this._recalculateMaxPageNumber();
        this.paginate();
    }

    _recalculateMaxPageNumber() {
        const total = this._filtered_entry_ids.length;
        this.max_page = Math.max(0, Math.ceil(total / this.entries_per_page) - 1);
        this._pagination_max_pagenumber.textContent = this.max_page + 1;
    }

    _genRowElem(fields) {
        const row_elem = document.createElement('div');
        row_elem.classList.add('datadisplay-body-row', 'd-flex');

        for(let i = 0; i < this._num_fields; i++) {
            const field_name = this.field_names[i];
            const value = (fields[i] ?? '');
            const cell_elem = document.createElement('div');
            cell_elem.classList.add('datadisplay-cell');
            cell_elem.setAttribute('title', value);
            cell_elem.textContent = value;
            if(this.field_width_overrides && (field_name in this.field_width_overrides)) {
                cell_elem.style.flex = this.field_width_overrides[field_name];
            }
            row_elem.append(cell_elem);
        }

        return row_elem;
    }

    _genCardTextElem(field_name, field_value) {
        const subtitle_wrapper = document.createElement('div');
        subtitle_wrapper.className = 'text-body-secondary';

        const field_name_elem = document.createElement('strong');
        field_name_elem.textContent = (field_name+': ');
        subtitle_wrapper.appendChild(field_name_elem);

        const field_value_elem = document.createElement('span');
        field_value_elem.textContent = field_value;
        subtitle_wrapper.appendChild(field_value_elem);

        return subtitle_wrapper;
    }

    _genCardElem(fields) {
        const BLANK_VALUE_PLACEHOLDER = '--';

        const card_elem = document.createElement('div');
        card_elem.classList.add('card');

        const card_body = document.createElement('div');
        card_body.className = 'card-body';
        card_elem.appendChild(card_body);

        let field_idx = 0;
        while(field_idx < fields.length) {
            const field_name = this.field_names[field_idx];
            const next_field_name_lower = (this.field_names[field_idx+1] ?? '').toLowerCase();
            const field_name_lower = field_name.toLowerCase();
            const field_value = (fields[field_idx]==='') ? BLANK_VALUE_PLACEHOLDER : fields[field_idx];

            if(DataDisplay.id_field_names.includes(field_name_lower) && DataDisplay.name_field_names.includes(next_field_name_lower)) {
                const title = document.createElement('h5');
                title.className = 'card-title';
                title.textContent = `[${field_value}] ${fields[field_idx+1]}`;
                card_body.appendChild(title);
                field_idx++;

            } else if(DataDisplay.name_field_names.includes(field_name_lower)) {
                const title = document.createElement('h5');
                title.className = 'card-title';
                title.textContent = field_value;
                card_body.appendChild(title);

            } else if(DataDisplay.comments_field_names.includes(field_name_lower) && field_value !== BLANK_VALUE_PLACEHOLDER) {
                const text = document.createElement('p');
                text.className = 'card-text';
                text.textContent = field_value;
                card_body.appendChild(text);

            } else if(field_value !== BLANK_VALUE_PLACEHOLDER) {
                card_body.appendChild(this._genCardTextElem(field_name, field_value));

            }

            field_idx++;
        }

        return card_elem;
    }

    _checkForEntries() {
        const hasEntries = this._filtered_entry_ids.length > 0;
        this._noentries_elem.style.display = hasEntries ? 'none' : 'block';
        this.table_elem.style.display = hasEntries ? '' : 'none';
    }

    setup() {
        const container = document.createElement('div');
        container.classList.add('datadisplay-container', 'sizing-container');
        container.innerHTML = `
            <div>
                <div class="datadisplay-heading datadisplay-sort-heading">
                    <div class="datadisplay-sort-container">
                        <span id="sort-by-text">Sort By:</span>
                        <select id="${this.id}_sort_select" class="datadisplay-sort-dropdown"></select>
                        <button id="${this.id}_sort_direction_down" type="button" class="datadisplay-sort-button"><svg class="fa-icon">${svgSortAmountAsc}</svg></button>
                        <button id="${this.id}_sort_direction_up" type="button" class="datadisplay-sort-button"><svg class="fa-icon">${svgSortAmountDesc}</svg></button>
                    </div>
                    <div class="datadisplay-sort-container">
                        <input id="${this.id}_search_box" class="datadisplay-search-box" type="text" placeholder="Search...">
                        <button id="${this.id}_search_clear_button" title="Reset search results" type="button" class="datadisplay-sort-button hidden"><svg class="fa-icon">${svgSquareX}</svg></button>
                        <a id="${this.id}_search_bookmark_button" title="Link to bookmark these results" target="_blank" type="button" class="datadisplay-sort-button hidden"><svg class="fa-icon">${svgBookmark}</svg></a>
                    </div>
                </div>
                <div class="my-2 fst-italic">${this.help_text}</div>
                <div id="${this.id}_table" class="datadisplay-table desktop-only">
                    <div class="dd-table-head"></div>
                    <div class="dd-table-body"></div>
                </div>
                <div id="${this.id}_cardlist" class="datadisplay-cardlist mobile-only"></div>
                <div id="${this.id}_noentries" class="datadisplay-noentries">No ${this.name}s to display</div>
                <br>
                <div class="datadisplay-heading" style="justify-content: space-between;">
                    <button id="${this.id}_pagination_last" class="datadisplay-pagination-button">Previous</button>
                    <span class="datadisplay-page-display">
                        Page <span id="${this.id}_pagination_pagenumber">1</span>
                        of <span id="${this.id}_pagination_max_pagenumber">1</span>
                        <strong class="datadisplay-page-display-divider">|</strong>
                        <span class="desktop-only">Showing </span><span id="${this.id}_pagination_showing"></span>
                    </span>
                    <button id="${this.id}_pagination_next" class="datadisplay-pagination-button">Next</button>
                </div>
            </div>
        `;
        this.parent_elem.appendChild(container);

        /* Get elements */ {
            this._container = container;
            this.table_elem = this._container.querySelector('#' + this.id + '_table');
            this.table_head_elem = this.table_elem.querySelector('.dd-table-head');
            this.table_body_elem = this.table_elem.querySelector('.dd-table-body');
            this.cardlist_elem = this._container.querySelector('#' + this.id + '_cardlist');
            this._noentries_elem = this._container.querySelector('#' + this.id + '_noentries');
            this._sort_select_elem = this._container.querySelector('#' + this.id + '_sort_select');
            this._sort_direction_down_elem = this._container.querySelector('#' + this.id + '_sort_direction_down');
            this._sort_direction_up_elem = this._container.querySelector('#' + this.id + '_sort_direction_up');
            this._search_box_elem = this._container.querySelector('#' + this.id + '_search_box');
            this._search_clear_button = this._container.querySelector('#' + this.id + '_search_clear_button');
            this._search_bookmark_button = this._container.querySelector('#' + this.id + '_search_bookmark_button');
            this._pagination_last = this._container.querySelector('#' + this.id + '_pagination_last');
            this._pagination_next = this._container.querySelector('#' + this.id + '_pagination_next');
            this._pagination_pagenumber = this._container.querySelector('#' + this.id + '_pagination_pagenumber');
            this._pagination_max_pagenumber = this._container.querySelector('#' + this.id + '_pagination_max_pagenumber');
            this._pagination_showing = this._container.querySelector('#' + this.id + '_pagination_showing');
        }

        this._container.style.setProperty('--heading-background-color', this.heading_background_color);
        this._container.style.setProperty('--heading-text-color', this.heading_text_color);
        this._container.style.setProperty('--entry-hover-lightness-percent', this.entry_hover_lightness_percent);

        /* Entry click handling */ {
            const entryClickHandler = ((e) => {
                const target = e.target;
                if(target.tagName === 'A' || target.closest('a') !== null) { return; }

                const target_row = target.closest('[data-entry-id]');
                if(target_row) {
                    this._entry_click_callback(tryParseIntStrict(target_row.dataset.entryId));
                }
            });
            this.table_body_elem.addEventListener('click', entryClickHandler);
            this.cardlist_elem.addEventListener('click', entryClickHandler);
        }

        /* Pagination Controls */ {
            this._pagination_last.disabled = true;
            this._pagination_last.addEventListener('click', () => {
                if(this.page <= 0) { return; }
                this.page--;
                this.paginate();
            });
            this._pagination_next.addEventListener('click', () => {
                if(this.page >= this.max_page) { return; }
                this.page++;
                this.paginate();
            });
        }

        /* Searching */ {
            const performSearch = () => this.search(this._search_box_elem.value);
            this._search_box_elem.addEventListener('input', performSearch);

            this._search_clear_button.addEventListener('click', () => {
                this._search_box_elem.value = '';
                performSearch();
                this.clearUrlSearch();
            });
        }

        /* Sorting */
        this._sort_direction_down_elem.classList.add('button-selected');
        const setSortDir = (dir) => {
            if(this.sort_direction === dir) { return; }
            this.sort_direction = dir;
            if(dir === 'desc') {
                this._sort_direction_down_elem.classList.add('button-selected');
                this._sort_direction_up_elem.classList.remove('button-selected');
            } else {
                this._sort_direction_up_elem.classList.add('button-selected');
                this._sort_direction_down_elem.classList.remove('button-selected');
            }
            this.reorder();
        };
        this._sort_direction_down_elem.addEventListener('click', () => setSortDir('desc'));
        this._sort_direction_up_elem.addEventListener('click', () => setSortDir('asc'));
        this.do_not_sort_by = this.do_not_sort_by.map((field) => toSnakeCase(field));


        /* Sort Select */ {
            for(const field of this.field_names) {
                const val = toSnakeCase(field);
                if(this.do_not_sort_by.includes(val)) { continue; }
                if(this.sort_by===null) { this.sort_by = val; }
                const opt = document.createElement('option');
                opt.value = val;
                opt.textContent = field;
                this._sort_select_elem.appendChild(opt);
            }
            this._sort_select_elem.value = this.sort_by;
            this._sort_select_elem.addEventListener('change', (e) => {
                this.sort_by = e.target.value;
                this.reorder();
            });
        }

        /* Heading */ {
            const heading_row = this._genRowElem(this.field_names, true);
            heading_row.classList.add('datadisplay-heading-row');
            heading_row.classList.remove('datadisplay-body-row');
            const heading_cells = heading_row.querySelectorAll('.datadisplay-cell');
            heading_cells.forEach(cell => {
                cell.addEventListener('click', () => {
                    const cell_name = toSnakeCase(cell.textContent);
                    if(this.do_not_sort_by.includes(cell_name)) { return; }
                    if(this.sort_by === cell_name) {
                        setSortDir(this.sort_direction === 'asc' ? 'desc' : 'asc');
                    } else {
                        this.sort_by = cell_name;
                        this._sort_select_elem.value = cell_name;
                        this.reorder();
                    }
                });
            });
            this.table_head_elem.appendChild(heading_row);
        }

        this._checkForEntries();
    }

    afterEntries() {
        this[this._needs_reorder ? 'reorder' : 'paginate']();
    }

    addEntry(id, values, sort_values) {
        assertNonblank(id);
        assertArray(values);

        const is_new = !this._entry_data[id];
        const search_str = values.map(v => v ? v.toString().toLowerCase() : '').join(' ');
        const matches_search = (this._search_text === '' || search_str.includes(this._search_text));

        this._entry_data[id] = {
            values: values,
            sort_values: sort_values || values,
            search_str: search_str
        };

        if(is_new) {
            this._all_entry_ids.push(id);
            if(matches_search) { this._filtered_entry_ids.push(id); }
            this._needs_reorder = true;

        } else {
            const currently_filtered = this._filtered_entry_ids.includes(id);
            if(matches_search && !currently_filtered) {
                this._filtered_entry_ids.push(id);
                this._recalculateMaxPageNumber();

            } else if(!matches_search && currently_filtered) {
                deleteValueFromArray(id, this._filtered_entry_ids);
                this._recalculateMaxPageNumber();
                if(this.page > this.max_page) { this.page = Math.max(0, this.max_page); }
            }
        }
    }

    removeEntry(id) {
        assertNonblank(id);
        if(!this._entry_data[id]) { return console.error('Entry ID not found.'); }

        delete this._entry_data[id];
        deleteValueFromArray(id, this._all_entry_ids);
        deleteValueFromArray(id, this._filtered_entry_ids);

        if(this.page > this.max_page) { this.page = Math.max(0, this.max_page); }

        this._recalculateMaxPageNumber();
        this.paginate();
        this._checkForEntries();
    }

    onEntryClick(callback) { this._entry_click_callback = callback; }

    onEntryRender(callback) { this._entry_render_callback = callback; }

    remapEntryId(old_entry_id, new_entry_id) {
        assertNonblank(old_entry_id);
        assertNonblank(new_entry_id);
        assertIn(old_entry_id, this._entry_data);

        const all_idx = this._all_entry_ids.indexOf(old_entry_id);
        if(all_idx !== -1) { this._all_entry_ids[all_idx] = new_entry_id; }

        const filter_idx = this._filtered_entry_ids.indexOf(old_entry_id);
        if(filter_idx !== -1) { this._filtered_entry_ids[filter_idx] = new_entry_id; }

        this._entry_data[new_entry_id] = this._entry_data[old_entry_id];
        delete this._entry_data[old_entry_id];

        this.afterEntries();
    }

    handleUrlSearch() {
        const url = new URL(window.location.href);
        const search_url_param = url.searchParams.get('search');
        if(search_url_param && search_url_param!=='') { this.search(search_url_param); }
    }

    clearUrlSearch() {
        const url = new URL(window.location.href);
        const search_url_param = url.searchParams.get('search');
        if(search_url_param) {
            url.searchParams.delete('search');
            window.history.replaceState(null, '', url.toString());
        }
    }
}